import {
  Alert,
  Badge,
  Button,
  Divider,
  Fieldset,
  Group,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import { Link } from "react-router-dom";
import type { LegacyCaseDetail } from "../../../types/legacyCases";
import { useLegacyReviewEditor } from "../../../hooks/useLegacyReviewEditor";
import LegacyPatientFields from "./LegacyPatientFields";
import LegacyCaseFields from "./LegacyCaseFields";
import LegacySourceControls from "./LegacySourceControls";
import LegacyNarrativeFields from "./LegacyNarrativeFields";
import LegacySpecimensFields from "./LegacySpecimensFields";
import LegacyDraftConflict from "./LegacyDraftConflict";
import LegacyImportConfirmation from "./LegacyImportConfirmation";

export default function LegacyReviewWorkspace({
  detail,
  importBlocked,
  checkImportCollision,
}: {
  detail: LegacyCaseDetail;
  importBlocked?: boolean;
  checkImportCollision?: () => Promise<boolean>;
}) {
  const editor = useLegacyReviewEditor(detail, checkImportCollision);
  return (
    <Paper withBorder p="md">
      <form onSubmit={editor.handleSave} noValidate>
        <Stack gap="lg">
          <Group justify="space-between">
            <Badge
              variant="light"
              color={editor.form.isDirty() ? "orange" : "blue"}
            >
              {editor.form.isDirty()
                ? "Unsaved changes"
                : editor.revision
                ? `Saved revision ${editor.revision}`
                : "Not saved"}
            </Badge>
            <Group>
              <Button
                type="submit"
                variant="default"
                loading={editor.saving.isPending}
              >
                Save draft
              </Button>
              <Button
                onClick={editor.handleImport}
                loading={editor.busy}
                disabled={importBlocked}
              >
                Import case
              </Button>
            </Group>
          </Group>
          <Alert color="blue">
            Review the original final report against the draft, select the
            patient record, then import the completed historical case. Fields
            marked * are required for import; incomplete drafts can be saved.
          </Alert>
          {editor.savedAt && (
            <Text size="sm" c="dimmed" role="status">
              Saved {new Date(editor.savedAt).toLocaleString()}
            </Text>
          )}
          {editor.error && (
            <Alert color="red" title="Review needs attention">
              <Stack gap="sm">
                <Text size="sm">{editor.error}</Text>
                {editor.errorCode === "patient_match_required" ||
                editor.errorCode === "patient_selection_changed" ? (
                  <Button
                    variant="light"
                    onClick={editor.handlePatientRecovery}
                  >
                    {editor.errorCode === "patient_selection_changed"
                      ? "Refresh patient choices"
                      : "Select existing patient"}
                  </Button>
                ) : editor.errorCode ===
                  "patient_candidates_review_required" ? (
                  <Button
                    variant="light"
                    onClick={editor.handleCandidateRecovery}
                  >
                    Refresh patient suggestions
                  </Button>
                ) : editor.errorCode === "case_number_exists" ? (
                  <Button
                    component={Link}
                    to={`/cases?search=${encodeURIComponent(
                      detail.caseNumber
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="light"
                  >
                    Review existing case
                  </Button>
                ) : (
                  <Button variant="light" onClick={editor.handleRecover}>
                    Compare with saved draft
                  </Button>
                )}
              </Stack>
            </Alert>
          )}
          <Fieldset disabled={editor.busy} variant="unstyled">
            <Stack gap="xl">
              <LegacySourceControls form={editor.form} detail={detail} />
              <Divider />
              <LegacyPatientFields
                key={editor.patientCandidatesRevision}
                form={editor.form}
                detail={detail}
                createCandidates={editor.patientCandidates.data ?? []}
                checkingCandidates={
                  editor.patientCandidates.isFetching ||
                  editor.patientCandidates.isPending
                }
                candidateError={editor.patientCandidates.error}
                onRefreshCandidates={editor.handleCandidateRecovery}
              />
              <Divider />
              <LegacyCaseFields form={editor.form} detail={detail} />
              <Divider />
              <LegacyNarrativeFields form={editor.form} />
              <Divider />
              <LegacySpecimensFields form={editor.form} />
            </Stack>
          </Fieldset>
          <Group justify="flex-end">
            <Button
              type="submit"
              variant="default"
              loading={editor.saving.isPending}
            >
              Save draft
            </Button>
            <Button
              onClick={editor.handleImport}
              loading={editor.busy}
              disabled={importBlocked}
            >
              Import case
            </Button>
          </Group>
        </Stack>
      </form>
      <LegacyImportConfirmation
        opened={editor.confirmationOpened}
        caseNumber={detail.caseNumber}
        revision={editor.revision}
        values={editor.form.values}
        pending={editor.importing.isPending}
        onClose={editor.handleCloseConfirmation}
        onConfirm={editor.handleConfirm}
        error={editor.error}
        importBlocked={importBlocked}
      />
      {editor.latest?.importedCaseId ? (
        <Alert color="blue" mt="md" title="This case has already been imported">
          <Button
            component={Link}
            to={`/cases/${editor.latest.importedCaseId}`}
          >
            Open imported case
          </Button>
        </Alert>
      ) : (
        editor.latest && (
          <LegacyDraftConflict
            latest={editor.latest}
            current={editor.form.values}
            onClose={editor.handleCloseConflict}
            onLoad={editor.handleLoadConflict}
            onKeep={editor.handleKeepConflict}
          />
        )
      )}
    </Paper>
  );
}
